/**
 * User-safe error model, compatible with the backend error model. UI code maps
 * an ApiError to a localized message — raw responses never reach the UI or the
 * logs.
 */
export type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'unexpected';

/** Формат CorrelationId backend; проверяется вся строка, включая завершающий перенос. */
export function correlationIdOf(value: unknown): string | undefined {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 64 &&
    !/[^A-Za-z0-9_.-]/.test(value)
    ? value
    : undefined;
}

export class ApiError extends Error {
  readonly correlationId?: string;
  constructor(
    readonly kind: ApiErrorKind,
    readonly status?: number,
    readonly code?: string,
    /**
     * `details` from the backend's error envelope (`ApiErrorResponse.details`).
     *
     * Some domain refusals are not just a status — they carry the content the caller
     * needs to act on. Point publication is the clear case: a 422
     * `PUBLICATION_RULE_VIOLATION` lists every unmet rule at once, so an operator can
     * fix the form in one pass. Dropping the body would reduce that to "не удалось".
     *
     * By contract the envelope holds no PII and no secrets, so it is safe to surface —
     * but it is still backend-shaped, so callers validate before rendering.
     */
    readonly details?: Record<string, unknown>,
    correlationId?: unknown,
  ) {
    super(kind);
    this.name = 'ApiError';
    this.correlationId = correlationIdOf(correlationId);
  }

  get isRetryable(): boolean {
    // A rate-limited request succeeds on its own once the window rolls over, so
    // it is retryable in the same sense as a transient server error. Callers
    // still need to back off rather than retry immediately.
    return this.kind === 'network' || this.kind === 'server' || this.kind === 'rate_limited';
  }
}

export function mapStatusToError(
  status: number,
  code?: string,
  details?: Record<string, unknown>,
  correlationId?: unknown,
): ApiError {
  switch (status) {
    // 400 is a validation failure on this platform, not an unknown outcome: the backend
    // answers it for `VALIDATION_FAILED`, bean-validation misses and path-parameter type
    // mismatches, and puts the offending fields in `details`. Leaving it in the default
    // branch classified real, explainable refusals as `unexpected`.
    case 400:
      return new ApiError('validation', status, code, details, correlationId);
    case 401:
      return new ApiError('unauthorized', status, code, details, correlationId);
    case 403:
      return new ApiError('forbidden', status, code, details, correlationId);
    case 404:
      return new ApiError('not_found', status, code, details, correlationId);
    case 409:
      return new ApiError('conflict', status, code, details, correlationId);
    case 422:
      return new ApiError('validation', status, code, details, correlationId);
    // 413 is the one refusal that does not come from the backend's error model: the
    // proxy in front of it stops the body by size and answers with its own page, so
    // there is no envelope and no code to forward. Left in the default branch it
    // surfaced as `unexpected` — an operator who attached a file larger than the limit
    // the screen states was told something went wrong. It is an input problem the user
    // can fix, so it belongs with the other ones, and it carries the code the backend
    // uses for the same refusal (`FILE_TOO_LARGE`, raised when a file passes the proxy
    // but exceeds the domain limit) so a screen can phrase it without caring which of
    // the two stopped the upload.
    case 413:
      return new ApiError('validation', status, code ?? 'FILE_TOO_LARGE', details, correlationId);
    // Back-office search and audit-trail reads are rate-limited per operator, and
    // the public onboarding endpoint is rate-limited per client — 429 is an
    // expected outcome with its own user-facing phrasing, not an unknown error.
    case 429:
      return new ApiError('rate_limited', status, code, details, correlationId);
    default:
      if (status >= 500) return new ApiError('server', status, code, details, correlationId);
      return new ApiError('unexpected', status, code, details, correlationId);
  }
}

/** One field-level complaint from the backend's validation response. */
export interface FieldError {
  field: string;
  message: string;
}

/**
 * Field errors carried by a validation failure.
 *
 * Two shapes reach the client. `MethodArgumentNotValidException` (bean validation) puts the
 * map directly in `details` — field name to message, with no wrapper key. A domain validation
 * exception (`MerchantApplicationValidationException` and its kin) wraps the same map as
 * `details.field_errors`, with a code per field (`operator_id: LACKS_…`). Both are read, so a
 * screen can name what to fix instead of showing a generic failure. Returns null when the
 * error is not a validation one or carries no usable pairs (a readiness refusal, for
 * instance, puts arrays there instead).
 */
export function fieldErrorsFromError(error: unknown): Array<FieldError> | null {
  if (!(error instanceof ApiError) || error.kind !== 'validation' || !error.details) return null;

  const wrapped = error.details.field_errors;
  const source =
    typeof wrapped === 'object' && wrapped !== null && !Array.isArray(wrapped)
      ? (wrapped as Record<string, unknown>)
      : error.details;
  const pairs = Object.entries(source).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
  return pairs.length > 0 ? pairs.map(([field, message]) => ({ field, message })) : null;
}
